<template>
    <div class="privacy-policy">
        <h1 class="privacy-title">
            <span class="title-icon">🔒</span>
            Privacy Policy
        </h1>
        
        <div class="privacy-intro">
            <strong>Foreststream Techglobal Limited</strong> (Address: MING SANG IND BLDG, 19-21 HING YIP STREET, Kwun Tong) 
            takes your privacy and the trust you place in us very seriously. This Privacy Policy describes 
            how we collect, use and disclose your personal information.
        </div>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">1</span>
                About this Privacy Policy
            </h2>
            <div class="section-content">
                <p>This Privacy Policy applies to your visit to or use of our websites, applications and other services, including activities that reference or link to this Privacy Policy (each a "Service").</p>
                <p>This Privacy Policy may be supplemented by other privacy statements, terms or notices that we provide to you. Foreststream Techglobal Limited owns or manages the Service as described herein, and is a provider to, by, or for the benefit of the Service.</p>
                <p>During the registration process for our products and services and in other registration forms, we will indicate the types of personal information you must provide and the types of personal information we request. You may choose not to submit the information we request, but that may limit or prevent Foreststream Techglobal Limited from providing services to you.</p>
                <p>For information on how we handle personal information contained in the content of our services (for example, in case law systems), please refer to our Fair Processing Statement.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">2</span>
                Information We Collect
            </h2>
            <div class="section-content">
                <p>We collect information about you in three ways: directly from what you enter, from third party sources, and through automated technology.</p>
                
                <h3 class="subsection-title">2.1 Data You Provide to Us</h3>
                <p>The types of personal information we collect directly from you, depending on how you interact with us and the Service, may include:</p>
                <ul class="privacy-list">
                    <li>Contact information, such as your name, email address, postal address, social media aliases and telephone numbers.</li>
                    <li>Account login credentials, such as username and password, password hints, and similar security information.</li>
                    <li>Other account registration and profile information, such as job title, educational/professional background and qualifications, and photographs.</li>
                    <li>Payment information, such as credit or debit card numbers.</li>
                    <li>Comments, feedback and other information you provide to us, such as search query data and questions, or information you send to customer support.</li>
                    <li>Interests and communication preferences, including preferred language.</li>
                </ul>

                <h3 class="subsection-title">2.2 Data from Your Organization</h3>
                <p>We may obtain personal information about you from the organization with which you are employed or affiliated in order to activate and manage your access and use of that organization's Service subscriptions, including:</p>
                <ul class="privacy-list">
                    <li>Contact information, such as your name and organizational email address, postal address and telephone number.</li>
                    <li>Other account registration information, such as job title.</li>
                    <li>Organization user ID.</li>
                </ul>

                <h3 class="subsection-title">2.3 Data from Other Sources</h3>
                <p>We may also obtain your contact information and other information about you from our affiliates and other third parties, including from sources such as:</p>
                <ul class="privacy-list">
                    <li>Social networks that you use to grant permission to the Service to access your data on one or more social networks.</li>
                    <li>Service providers who can help us identify locations to target certain products to your location.</li>
                    <li>Businesses with which we partner to provide co-branded services or participate in joint marketing campaigns.</li>
                    <li>Publicly available sources and data providers through which we obtain data to verify or supplement existing information.</li>
                </ul>

                <h3 class="subsection-title">2.4 Data Generated When Using the Service</h3>
                <p>The Service may automatically collect information about how you and your device interact with the Service, including:</p>
                <ul class="privacy-list">
                    <li>Computer, device and connection information, such as IP address, browser type and version, operating system and other software installed on your device, mobile platform and device unique identifiers, and other technical identifiers, error reports and performance data.</li>
                    <li>Usage data, such as the features you use, the settings you select, your URL clickstream data, including the date and time stamps of referring pages and exit pages, the search terms you use, and the pages you access or search for on the Services.</li>
                    <li>For the Education Service, the course modules and test questions you viewed, answered or completed.</li>
                    <li>For location-aware Services, the region, city or town where your device is located in order to provide you with content that is more relevant to your location.</li>
                </ul>
                <p>We collect this data through our servers and through the use of cookies and other technologies, which are small text files placed on your hard drive by a web server in the domain and can be read by that server. We may use cookies and other technologies to store your preferences and settings, to help you log in, to deliver targeted advertising, and to analyze site operations. You can control cookies through your browser settings and other tools.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">3</span>
                How We Use Your Information
            </h2>
            <div class="section-content">
                <p>Depending on how you interact with us and the Service, we may use your personal information to:</p>
                <ul class="privacy-list">
                    <li>Provide, activate and manage your access and use of the Service.</li>
                    <li>Process and fulfill requests, orders, downloads, subscriptions or other transactions.</li>
                    <li>Provide technical, product and other support to help keep the Service up and running, secure and stable.</li>
                    <li>Enhance and improve the Service and our other products, activities and services.</li>
                    <li>Provide you with customized content and other personalized settings.</li>
                    <li>Respond to your requests, inquiries, comments and concerns.</li>
                    <li>Inform you of changes, updates and other announcements.</li>
                    <li>Communicate targeted advertising and promotional messages.</li>
                </ul>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">4</span>
                Sharing Your Information
            </h2>
            <div class="section-content">
                <h3 class="subsection-title">4.1 Your Organization</h3>
                <p>If you access the Service through a subscription managed by your organization, your personal information may be accessed or shared with authorized administrators.</p>

                <h3 class="subsection-title">4.2 Our Group Companies and Service Providers</h3>
                <p>Depending on the Services provided, we may share information with our group companies and service providers, including payment processors, customer support, email service providers, IT service providers, and marketing service providers.</p>

                <h3 class="subsection-title">4.3 For Legal Reasons</h3>
                <p>We may also disclose your personal information when we believe it is reasonably necessary to comply with any applicable law, regulation, legal process or other legal obligation.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">5</span>
                Basis for Processing Information
            </h2>
            <div class="section-content">
                <p>When we collect or process personal information under applicable data protection laws, we do so for the purposes of:</p>
                <ul class="privacy-list">
                    <li>Providing a "service", completing a transaction, or performing a contract with you.</li>
                    <li>Complying with applicable laws or other legal obligations.</li>
                    <li>Performing tasks that are in the public interest.</li>
                    <li>Operating our business and protecting the security of our systems.</li>
                </ul>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">6</span>
                Data Retention
            </h2>
            <div class="section-content">
                <p>We will retain your personal information for as long as necessary to provide the Services, complete the transactions you have requested, or for other necessary purposes, including complying with legal obligations, maintaining business and financial records, resolving disputes, and enforcing agreements.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">7</span>
                Where Information is Processed
            </h2>
            <div class="section-content">
                <p>We may store and process your personal information in your region or in other countries where our affiliates and service providers maintain servers and facilities, including Australia, mainland China, France, Germany, Hong Kong, India, Ireland, Mauritius, the Netherlands, the Philippines, Singapore, the United Kingdom and the United States.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">8</span>
                Data Security
            </h2>
            <div class="section-content">
                <p>We will implement a variety of technical and organizational measures to ensure a level of security commensurate with the risk to the personal information we process. These measures are designed to ensure the integrity, confidentiality and availability of personal information.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">9</span>
                Children's Privacy
            </h2>
            <div class="section-content">
                <p>We do not knowingly collect information from children under the age of 14, nor do we target children under the age of 14 to the Service.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">10</span>
                Your Rights
            </h2>
            <div class="section-content">
                <p>You have the right, free of charge and in accordance with applicable privacy and data protection laws, to request:</p>
                <ul class="privacy-list">
                    <li>Access to your personal information.</li>
                    <li>To correct or erase your personal information.</li>
                    <li>To restrict or object to our processing of your personal information.</li>
                    <li>To transfer your personal information.</li>
                </ul>
                <p>If you wish to exercise these rights, please submit your request in writing or email it to <a href="mailto:xhskyxgs@gmail.com" class="privacy-link">xhskyxgs@gmail.com</a>. We will respond to your request in accordance with applicable law.</p>
            </div>
        </section>

        <section class="privacy-section">
            <h2 class="section-title">
                <span class="section-number">11</span>
                Contact Information
            </h2>
            <div class="section-content">
                <p>If you have any questions, comments, complaints or requests regarding this Privacy Policy, please contact us:</p>
                <div class="contact-box">
                    <p><strong>Email:</strong> <a href="mailto:xhskyxgs@gmail.com" class="privacy-link">xhskyxgs@gmail.com</a></p>
                    <p><strong>Technical Support:</strong> <a href="mailto:forsupport@foreststreamtech.top" class="privacy-link">forsupport@foreststreamtech.top</a></p>
                </div>
            </div>
        </section>

        <div class="effective-date">
            <span class="date-label">Effective Date</span>
            <span class="date-value">November 1st, 2026</span>
        </div>

        <div class="copyright">
            <p v-if="isGame1123026">© 2026 Foreststream Techglobal Limited. All rights reserved. Technical support provided by G.S.A. CONCEPT 2017 SRL.</p>
            <p v-else>© 2026 Foreststream Techglobal Limited. All rights reserved. Technical support provided by AUTOLLC.</p>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
    name: 'PrivacyPolicyContent',
    computed: {
        ...mapState({
            appConfig: state => state.app.appConfig,
        }),
        isGame1123026() {
            return `${this.appConfig.game_id}` === '1123026'
        }
    }
}
</script>

<style scoped>
.privacy-policy {
    height: 100%;
    /* padding: 24px; */
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    line-height: 1.8;
    color: #334155;
    /* overflow-y: auto; */
}

/* 标题样式 */
.privacy-title {
    font-size: 28px;
    font-weight: 700;
    color: #1e293b;
    text-align: center;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 3px solid #3b82f6;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.title-icon {
    font-size: 32px;
}

/* 简介卡片 */
.privacy-intro {
    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
    border-left: 4px solid #3b82f6;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 32px;
    font-size: 15px;
    line-height: 1.8;
    color: #1e40af;
}

/* 章节样式 */
.privacy-section {
    margin-bottom: 32px;
    background: #ffffff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
    transition: box-shadow 0.3s ease;
}

.privacy-section:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
}

.section-title {
    font-size: 20px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
}

.section-number {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border-radius: 50%;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
}

/* 子标题 */
.subsection-title {
    font-size: 16px;
    font-weight: 600;
    color: #334155;
    margin-top: 20px;
    margin-bottom: 12px;
    padding-left: 12px;
    border-left: 3px solid #60a5fa;
}

/* 内容样式 */
.section-content p {
    margin-bottom: 12px;
    text-align: justify;
}

/* 列表样式 */
.privacy-list {
    margin: 16px 0;
    padding-left: 0;
    list-style: none;
}

.privacy-list li {
    position: relative;
    padding-left: 24px;
    margin-bottom: 10px;
    line-height: 1.7;
}

.privacy-list li::before {
    content: '•';
    position: absolute;
    left: 8px;
    color: #3b82f6;
    font-weight: bold;
    font-size: 18px;
}

/* 链接样式 */
.privacy-link {
    color: #2563eb;
    text-decoration: none;
    font-weight: 500;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;
}

.privacy-link:hover {
    border-bottom-color: #2563eb;
}

/* 联系信息卡片 */
.contact-box {
    background: #f1f5f9;
    border-radius: 8px;
    padding: 16px 20px;
    margin-top: 12px;
}

.contact-box p {
    margin-bottom: 8px;
}

.contact-box p:last-child {
    margin-bottom: 0;
}

/* 生效日期 */
.effective-date {
    margin-top: 40px;
    padding: 20px;
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
    border-radius: 12px;
    text-align: center;
    color: white;
}

.date-label {
    display: block;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.8;
    margin-bottom: 4px;
}

.date-value {
    display: block;
    font-size: 18px;
    font-weight: 600;
}

/* 版权信息 */
.copyright {
    margin-top: 24px;
    padding: 16px 20px;
    background: #f8fafc;
    border-radius: 12px;
    text-align: center;
    border-top: 1px solid #e2e8f0;
}

.copyright p {
    font-size: 13px;
    color: #64748b;
    line-height: 1.6;
    margin: 0;
}

/* 滚动条样式 */
.privacy-policy::-webkit-scrollbar {
    width: 8px;
}

.privacy-policy::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 4px;
}

.privacy-policy::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 4px;
}

.privacy-policy::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
</style>
